import { useTranslation } from "react-i18next";
import { SectionLabel } from "./primitives";

/**
 * [03] Pipeline. Linear flow from drone to scout list with terse
 * one-line descriptions per stage.
 */
export function Pipeline() {
  const { t } = useTranslation();
  return (
    <section id="pipeline" className="relative border-b border-[color:var(--color-app-edge)] bg-[color:var(--color-app-card)]/30">
      <div className="relative mx-auto max-w-[1320px] px-6 lg:px-10 py-24 md:py-28">
        <SectionLabel
          index={t("pipeline.section.index")}
          title={t("pipeline.section.title")}
          kicker={t("pipeline.section.kicker")}
        />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-16">
          <h2 className="text-display text-5xl md:text-6xl">
            {t("pipeline.headline.before")}{" "}
            <span className="text-accent text-accent-water">
              {t("pipeline.headline.accent")}
            </span>
            {t("pipeline.headline.after")}
          </h2>
          <div className="md:pl-8 md:border-l md:border-[color:var(--color-app-edge)] text-[color:var(--color-app-text)]/75 leading-relaxed flex items-center">
            <p>{t("pipeline.intro")}</p>
          </div>
        </div>

        <div className="relative">
          {/* Connector track */}
          <div className="hidden lg:block absolute top-[44px] left-[6%] right-[6%] h-px bg-[color:var(--color-app-edge-2)]" />
          <div
            className="hidden lg:block absolute top-[42px] left-[6%] right-[6%] h-[3px]"
            style={{
              background:
                "linear-gradient(to right, transparent, var(--color-meadow), transparent)",
              backgroundSize: "30% 100%",
              backgroundRepeat: "no-repeat",
              animation: "beamSweep 6s linear infinite",
            }}
          />
          <style>{`
            @keyframes beamSweep {
              0%   { background-position: -30% 0; }
              100% { background-position: 130% 0; }
            }
            @media (prefers-reduced-motion: reduce) {
              [data-pipeline-beam] { animation: none !important; }
            }
          `}</style>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-y-10 gap-x-3 lg:gap-x-6">
            <PipelineStage n="01" stageKey="capture" icon={<IconCapture />} />
            <PipelineStage n="02" stageKey="ingest"  icon={<IconIngest />} />
            <PipelineStage n="03" stageKey="stitch"  icon={<IconStitch />} />
            <PipelineStage n="04" stageKey="tile"    icon={<IconTile />} />
            <PipelineStage n="05" stageKey="vision"  icon={<IconVision />} highlight />
            <PipelineStage n="06" stageKey="triage"  icon={<IconTriage />} />
          </div>
        </div>

      </div>
    </section>
  );
}

function PipelineStage({
  n,
  stageKey,
  icon,
  highlight,
}: {
  n: string;
  stageKey: "capture" | "ingest" | "stitch" | "tile" | "vision" | "triage";
  icon: React.ReactNode;
  highlight?: boolean;
}) {
  const { t } = useTranslation();
  return (
    <PipelineStageInner
      n={n}
      name={t(`pipeline.stages.${stageKey}.name`)}
      meta={t(`pipeline.stages.${stageKey}.meta`)}
      desc={t(`pipeline.stages.${stageKey}.desc`)}
      icon={icon}
      highlight={highlight}
    />
  );
}

function PipelineStageInner({
  n,
  name,
  meta,
  desc,
  icon,
  highlight,
}: {
  n: string;
  name: string;
  meta: string;
  desc: string;
  icon: React.ReactNode;
  highlight?: boolean;
}) {
  return (
    <div className="relative">
      {/* Node circle */}
      <div className="relative z-10 flex justify-center mb-5">
        <div
          className={[
            "w-[88px] h-[88px] flex items-center justify-center",
            "border bg-[color:var(--color-app-bg)]",
            highlight
              ? "border-[color:var(--color-amber)] text-[color:var(--color-amber)] shadow-[0_0_24px_-4px_var(--color-amber)]"
              : "border-[color:var(--color-app-edge-2)] text-[color:var(--color-app-text)]/85",
          ].join(" ")}
        >
          {icon}
        </div>
      </div>
      <div className="text-center">
        <div className="label-mono mb-1">
          <span className="bracket">{n}</span>
        </div>
        <div className="font-display text-2xl mb-1">
          {name}
        </div>
        <div className="coord-mono opacity-60 mb-2">{meta}</div>
        <div className="text-sm text-[color:var(--color-app-text)]/75 max-w-[200px] mx-auto leading-snug">
          {desc}
        </div>
      </div>
    </div>
  );
}

/* ── Inline SVG icons (32×32 viewBox) ────────────────────────────── */

function IconCapture() {
  return (
    <svg viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth={1.4} className="w-9 h-9">
      <circle cx={6}  cy={6}  r={3} />
      <circle cx={26} cy={6}  r={3} />
      <circle cx={6}  cy={26} r={3} />
      <circle cx={26} cy={26} r={3} />
      <rect x={12} y={12} width={8} height={8} rx={1} />
      <path d="M9 9l3 3M23 9l-3 3M9 23l3-3M23 23l-3-3" strokeLinecap="round" />
    </svg>
  );
}
function IconIngest() {
  return (
    <svg viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth={1.4} className="w-9 h-9">
      <path d="M16 4v18" strokeLinecap="round" />
      <path d="M9 13l7 7 7-7" strokeLinecap="round" strokeLinejoin="round" />
      <rect x={4}  y={24} width={24} height={4} />
    </svg>
  );
}
function IconStitch() {
  return (
    <svg viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth={1.4} className="w-9 h-9">
      <rect x={4}  y={4} width={10} height={10} />
      <rect x={18} y={4} width={10} height={10} />
      <rect x={4}  y={18} width={10} height={10} />
      <rect x={18} y={18} width={10} height={10} />
      <path d="M14 9h4M14 23h4M9 14v4M23 14v4" stroke="currentColor" strokeDasharray="1.5 1.5" />
    </svg>
  );
}
function IconTile() {
  return (
    <svg viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth={1.4} className="w-9 h-9">
      <rect x={2}  y={20} width={28} height={9} />
      <rect x={6}  y={11} width={20} height={9} />
      <rect x={10} y={2}  width={12} height={9} />
    </svg>
  );
}
function IconVision() {
  return (
    <svg viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth={1.4} className="w-9 h-9">
      <circle cx={16} cy={16} r={6} />
      <path d="M2 16c2.5-5 7.5-9 14-9s11.5 4 14 9c-2.5 5-7.5 9-14 9S4.5 21 2 16z" />
      <circle cx={16} cy={16} r={2} fill="currentColor" />
    </svg>
  );
}
function IconTriage() {
  return (
    <svg viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth={1.4} className="w-9 h-9">
      <path d="M5 9h10M5 16h14M5 23h8" strokeLinecap="round" />
      <path d="M22 21l3 3 5-6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
